import { useEffect, useState, type ComponentType } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import {
  deriveWorkflowProgress,
  type Campaign,
  type ExtractedImage,
  type FieldSession,
  type Model,
  type Video,
  type VideoStatus,
  type WorkflowStepId,
  type WorkflowStepState,
} from '@ddp/shared';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Flow from '@carbon/icons-react/es/Flow';
import CloudUpload from '@carbon/icons-react/es/CloudUpload';
import Tag from '@carbon/icons-react/es/Tag';
import MachineLearningModel from '@carbon/icons-react/es/MachineLearningModel';
import Rocket from '@carbon/icons-react/es/Rocket';
import ChevronRight from '@carbon/icons-react/es/ChevronRight';
import ArrowRight from '@carbon/icons-react/es/ArrowRight';

import { apiFetch } from '../api/client.js';
import { useT, type MessageKey } from '../i18n/index.js';
import { PageHeader } from '../components/PageHeader.js';
import { HelpHint } from '../components/HelpHint.js';
import { ProjectsPanel } from '../features/projects/ProjectsPanel.js';
import { FleetSection } from '../features/hardware/FleetSection.js';
import { VideoThumbnail } from '../features/video/VideoThumbnail.js';

/** Intention visuelle d'un statut vidéo : couleur (ok/warn/danger/neutre) + libellé i18n. */
const VIDEO_STATUS_META: Record<VideoStatus, { cls: string; key: MessageKey }> = {
  PENDING: { cls: 'neutral', key: 'data.status.pending' },
  UPLOADING: { cls: 'warn', key: 'data.status.uploading' },
  UPLOADED: { cls: 'neutral', key: 'data.status.uploaded' },
  PROCESSING: { cls: 'warn', key: 'data.status.processing' },
  READY: { cls: 'ok', key: 'data.status.ready' },
  FAILED: { cls: 'danger', key: 'data.status.failed' },
};

/** Un ratio `[0,1]` en pourcentage entier, ou « — » s'il est absent. */
const pct = (value: number | undefined): string =>
  typeof value === 'number' ? `${Math.round(value * 100)}%` : '—';

/** Barème visuel du mAP : vert au-dessus de 50 %, ambre au-dessus de 30 %, neutre en dessous. */
function mapBarClass(mAP: number): string {
  if (mAP >= 0.5) return 'ok';
  if (mAP >= 0.3) return 'warn';
  return 'neutral';
}

/**
 * Tableau de bord récapitulatif (PRD NFR-27, section « Overview »). Vue d'un coup d'œil de ce que
 * contient l'organisation et point d'entrée vers les quatre grandes fonctionnalités.
 */
export function OverviewPage() {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [fieldSessions, setFieldSessions] = useState<FieldSession[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [images, setImages] = useState<ExtractedImage[]>([]);
  const [models, setModels] = useState<Model[]>([]);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [p, m, v, i, md] = await Promise.all([
          apiFetch<Campaign[]>('/campaigns'),
          apiFetch<FieldSession[]>('/field-sessions'),
          apiFetch<Video[]>('/videos'),
          apiFetch<ExtractedImage[]>('/images'),
          apiFetch<Model[]>('/models'),
        ]);
        if (!active) return;
        setCampaigns(p);
        setFieldSessions(m);
        setVideos(v);
        setImages(i);
        setModels(md);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description={t('common.loading')} />
      </section>
    );
  }

  // Modèles évalués, du meilleur mAP au moins bon (pour la barre classée).
  const evaluatedModels = [...models]
    .filter((m) => m.metrics && typeof (m.metrics as { mAP?: number }).mAP === 'number')
    .sort(
      (a, b) =>
        ((b.metrics as { mAP?: number }).mAP ?? 0) - ((a.metrics as { mAP?: number }).mAP ?? 0),
    );
  const recentVideos = [...videos]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  // Bascule B → D. Tant que l'organisation est vide (ni vidéo ni modèle), on enseigne la chaîne
  // avec le pipeline guidé ; dès qu'elle vit, on passe à la barre de commande compacte, moins
  // pédagogique mais plus rapide pour l'habitué.
  const isActive = videos.length > 0 || models.length > 0;

  // Avancement du parcours, dérivé des mêmes signaux que la page Parcours (contrat unique, ADR-0020).
  // La bande partage cette lecture : « fait » veut dire la même chose ici et là-bas.
  const annotatedVideoCount = videos.filter((v) => v.annotationCount > 0).length;
  const readyModelCount = models.filter((m) => m.status === 'READY').length;
  const evaluatedModelCount = models.filter(
    (m) => m.metrics && typeof (m.metrics as { mAP?: number }).mAP === 'number',
  ).length;
  const progress = deriveWorkflowProgress({
    videoCount: videos.length,
    annotatedVideoCount,
    readyModelCount,
    evaluatedModelCount,
  });
  const stepState = new Map<WorkflowStepId, WorkflowStepState>(
    progress.steps.map((s) => [s.id, s.state]),
  );

  return (
    <section className="ddp-page__section ddp-overview">
      <PageHeader title={t('overview.title')} lead={t('overview.intro')} tourScope="overview" />

      {/* Liens de section accrochés (ancre vers chaque bloc de la page), comme sur les autres pages. */}
      <nav className="ddp-page-subnav" aria-label={t('overview.title')}>
        <a href="#overview-projects">{t('overview.projects.title')}</a>
        <a href="#overview-fleet">{t('fleet.title')}</a>
        <a href="#overview-videos">{t('overview.recentVideos')}</a>
        <a href="#overview-models">{t('overview.evaluatedModels')}</a>
      </nav>

      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {isActive ? (
        <CommandBand videos={videos} recentVideo={recentVideos[0]} />
      ) : (
        <PipelineBand
          stepState={stepState}
          videoCount={videos.length}
          annotatedVideoCount={annotatedVideoCount}
          readyModelCount={readyModelCount}
        />
      )}

      <div className="ddp-overview__tiles">
        <StatTile to="/data" label={t('overview.tile.campaigns')} value={campaigns.length} />
        <StatTile to="/data" label={t('overview.tile.sessions')} value={fieldSessions.length} />
        {/* Vidéos = tuile vedette : liseré d'accent, pas d'à-plat. */}
        <StatTile to="/data" label={t('overview.tile.videos')} value={videos.length} hero />
        <StatTile to="/model" label={t('overview.tile.images')} value={images.length} />
        <StatTile to="/model" label={t('overview.tile.models')} value={models.length} />
      </div>

      {/* Overview = vue d'ensemble en lecture seule : on liste, la création/gestion se fait sur les
          pages dédiées (Projets → Data, Matériels → Deploy). */}
      <ProjectsPanel readOnly />

      <FleetSection readOnly />

      <div className="ddp-overview__cols">
        <div className="ddp-overview__col">
          <div className="ddp-overview__section-head">
            <h2 id="overview-videos" className="ddp-overview__subtitle">
              {t('overview.recentVideos')}
            </h2>
            {recentVideos.length > 0 && (
              <RouterLink to="/data" className="ddp-overview__seeall">
                {t('overview.seeAll')}
              </RouterLink>
            )}
          </div>
          {recentVideos.length === 0 ? (
            <p className="ddp-overview__empty">
              {t('overview.noVideos')}{' '}
              <RouterLink to="/data">{t('overview.noVideosCta')}</RouterLink>
            </p>
          ) : (
            <ul className="ddp-overview__panel">
              {recentVideos.map((video) => {
                const meta = VIDEO_STATUS_META[video.status];
                return (
                  <li key={video.id} className="ddp-vrow">
                    <VideoThumbnail
                      videoId={video.id}
                      status={video.status}
                      className="ddp-vrow__thumb"
                      mediaClassName="ddp-vrow__thumb-media"
                      emptyClassName="ddp-vrow__thumb-empty"
                    />
                    <div className="ddp-vrow__body">
                      <RouterLink
                        to="/videos/$videoId"
                        params={{ videoId: video.id }}
                        className="ddp-vrow__title"
                      >
                        {video.title}
                      </RouterLink>
                      <span className="ddp-vrow__meta">
                        {formatDate(video.createdAt)}
                        {video.annotationCount > 0 && ` · ${video.annotationCount} annotations`}
                      </span>
                    </div>
                    <span className={`ddp-status ddp-status--${meta.cls}`}>{t(meta.key)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="ddp-overview__col">
          <div className="ddp-overview__section-head">
            <h2 id="overview-models" className="ddp-overview__subtitle">
              {t('overview.evaluatedModels')}
              <HelpHint textKey="help.overview.evaluatedModels" />
            </h2>
            {evaluatedModels.length > 0 && (
              <RouterLink to="/model" className="ddp-overview__seeall">
                {t('overview.compare')}
              </RouterLink>
            )}
          </div>
          {evaluatedModels.length === 0 ? (
            <p className="ddp-overview__empty">
              {t('overview.noEval')} <RouterLink to="/model">{t('overview.noEvalCta')}</RouterLink>
            </p>
          ) : (
            <ul className="ddp-overview__panel">
              {evaluatedModels.slice(0, 5).map((model) => {
                const m = model.metrics as {
                  mAP?: number;
                  precision?: number;
                  recall?: number;
                  imageCount?: number;
                } | null;
                const mAP = m?.mAP ?? 0;
                return (
                  <li key={model.id} className="ddp-mrow">
                    <div className="ddp-mrow__top">
                      <RouterLink to="/model" className="ddp-mrow__name">
                        {model.name} · v{model.version}
                      </RouterLink>
                      <span className="ddp-mrow__val">
                        mAP <b>{pct(mAP)}</b>
                      </span>
                    </div>
                    <div className={`ddp-mbar ddp-mbar--${mapBarClass(mAP)}`}>
                      <span style={{ width: `${Math.round(mAP * 100)}%` }} />
                    </div>
                    <div className="ddp-mrow__sub">
                      P {pct(m?.precision)} · R {pct(m?.recall)}
                      {typeof m?.imageCount === 'number' ? ` · ${m.imageCount} images` : ''}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

/** Une étape du pipeline guidé : icône, cap vers une zone, et compte à afficher quand elle est faite. */
interface PipelineStep {
  id: WorkflowStepId;
  to: string;
  icon: ComponentType<{ size?: number }>;
  titleKey: MessageKey;
  descKey: MessageKey;
  /** Décompte tangible affiché lorsque l'étape est satisfaite (sinon « à faire »/« à venir »). */
  count: number | null;
  unitKey: MessageKey | null;
}

/** Traduit l'état canonique d'une étape (ADR-0020) en intention visuelle du point de la bande. */
function toneOf(state: WorkflowStepState | undefined): 'ok' | 'todo' | 'soon' {
  if (state === 'done') return 'ok';
  if (state === 'locked') return 'soon';
  return 'todo';
}

/**
 * Direction B — pipeline guidé. Affiché tant que l'organisation est vide : il enseigne la chaîne
 * Données → Annotation → Modèle → Déploiement et donne un point d'entrée par étape, coiffé d'un
 * appel « workflow complet » qui ouvre le vrai parcours pas-à-pas (ADR-0020). L'état de chaque
 * étape vient du contrat partagé, pas d'un calcul local — « fait » y est le même mot que sur la
 * page Parcours.
 */
function PipelineBand({
  stepState,
  videoCount,
  annotatedVideoCount,
  readyModelCount,
}: {
  stepState: Map<WorkflowStepId, WorkflowStepState>;
  videoCount: number;
  annotatedVideoCount: number;
  readyModelCount: number;
}) {
  const t = useT();
  const steps: PipelineStep[] = [
    {
      id: 'data',
      to: '/data',
      icon: CloudUpload,
      titleKey: 'overview.guide.step.data',
      descKey: 'overview.guide.step.data.desc',
      count: videoCount,
      unitKey: 'overview.tile.videos',
    },
    {
      id: 'annotation',
      to: '/data',
      icon: Tag,
      titleKey: 'overview.guide.step.annotation',
      descKey: 'overview.guide.step.annotation.desc',
      count: annotatedVideoCount,
      unitKey: 'workflow.count.annotated',
    },
    {
      id: 'model',
      to: '/model',
      icon: MachineLearningModel,
      titleKey: 'overview.guide.step.model',
      descKey: 'overview.guide.step.model.desc',
      count: readyModelCount,
      unitKey: 'overview.tile.models',
    },
    {
      id: 'deploy',
      to: '/deploy',
      icon: Rocket,
      titleKey: 'overview.guide.step.deploy',
      descKey: 'overview.guide.step.deploy.desc',
      count: null,
      unitKey: null,
    },
  ];

  return (
    <section
      className="ddp-guide ddp-guide--pipeline"
      aria-label={t('overview.guide.workflow.title')}
    >
      <p className="ddp-guide__lead">{t('overview.guide.lead.new')}</p>

      {/* L'appel « workflow complet » ouvre le vrai parcours pas-à-pas. */}
      <RouterLink to="/parcours" className="ddp-guide__cta">
        <span className="ddp-guide__cta-icon" aria-hidden="true">
          <Flow size={24} />
        </span>
        <span className="ddp-guide__cta-text">
          <b>{t('overview.guide.workflow.title')}</b>
          <small>{t('overview.guide.workflow.desc')}</small>
        </span>
        <span className="ddp-guide__cta-go">
          {t('overview.guide.workflow.cta')}
          <ArrowRight size={16} />
        </span>
      </RouterLink>

      <ol className="ddp-guide__pipe">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const tone = toneOf(stepState.get(step.id));
          const state =
            tone === 'soon'
              ? t('overview.guide.state.soon')
              : tone === 'ok' && step.count && step.count > 0 && step.unitKey
                ? `${step.count} ${t(step.unitKey).toLowerCase()}`
                : t('overview.guide.state.todo');
          return (
            <li key={step.id} className="ddp-guide__stage-wrap">
              <RouterLink to={step.to} className="ddp-guide__stage">
                <span className="ddp-guide__stage-icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="ddp-guide__stage-title">{t(step.titleKey)}</span>
                <span className="ddp-guide__stage-desc">{t(step.descKey)}</span>
                <span className={`ddp-guide__stage-state ddp-guide__stage-state--${tone}`}>
                  <span className="ddp-guide__dot" aria-hidden="true" />
                  {state}
                </span>
              </RouterLink>
              {i < steps.length - 1 && (
                <ChevronRight size={20} className="ddp-guide__chevron" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * Direction D — barre de commande + reprise. Affichée dès que l'organisation vit : les quatre
 * actions en boutons compacts (le workflow en tête), suivies d'une ligne « reprendre » déduite
 * des données réelles (vidéos prêtes non annotées, sinon la vidéo la plus récente).
 */
function CommandBand({ videos, recentVideo }: { videos: Video[]; recentVideo?: Video }) {
  const t = useT();

  // Indice de reprise, par priorité : d'abord ce qui attend une action (annotation), sinon un
  // simple raccourci vers la dernière vidéo. Un seul indice pour ne pas encombrer la ligne.
  const toAnnotate = videos.filter((v) => v.status === 'READY' && v.annotationCount === 0).length;
  const resume =
    toAnnotate > 0
      ? {
          to: '/data' as const,
          params: undefined,
          text: `${toAnnotate} ${t('overview.tile.videos').toLowerCase()} ${t('overview.guide.resume.toAnnotate')}`,
        }
      : recentVideo
        ? {
            to: '/videos/$videoId' as const,
            params: { videoId: recentVideo.id },
            text: `${t('overview.guide.resume.open')} « ${recentVideo.title} »`,
          }
        : null;

  return (
    <section className="ddp-guide ddp-guide--command" aria-label={t('overview.guide.lead.active')}>
      <div className="ddp-guide__cmdbar">
        <RouterLink to="/parcours" className="ddp-guide__cmd ddp-guide__cmd--primary">
          <Flow size={16} />
          {t('overview.guide.workflow.title')}
        </RouterLink>
        <span className="ddp-guide__cmd-sep" aria-hidden="true" />
        <RouterLink to="/data" className="ddp-guide__cmd">
          <CloudUpload size={16} />
          {t('overview.guide.cmd.data')}
        </RouterLink>
        <RouterLink to="/model" className="ddp-guide__cmd">
          <MachineLearningModel size={16} />
          {t('overview.guide.cmd.model')}
        </RouterLink>
        <RouterLink to="/deploy" className="ddp-guide__cmd">
          <Rocket size={16} />
          {t('overview.guide.cmd.deploy')}
        </RouterLink>
      </div>

      {resume && (
        <p className="ddp-guide__resume">
          <span className="ddp-guide__resume-pill">{t('overview.guide.resume.label')}</span>
          {resume.to === '/videos/$videoId' ? (
            <RouterLink to={resume.to} params={resume.params}>
              {resume.text}
              <ArrowRight size={14} />
            </RouterLink>
          ) : (
            <RouterLink to={resume.to}>
              {resume.text}
              <ArrowRight size={14} />
            </RouterLink>
          )}
        </p>
      )}
    </section>
  );
}

function StatTile({
  to,
  label,
  value,
  hero = false,
}: {
  to: string;
  label: string;
  value: number;
  hero?: boolean;
}) {
  return (
    <RouterLink to={to} className={`ddp-tile${hero ? ' ddp-tile--hero' : ''}`}>
      <span className="ddp-tile__value">{value}</span>
      <span className="ddp-tile__label">{label}</span>
    </RouterLink>
  );
}

function formatDate(value: Date | string): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
